import { format } from 'date-fns';

/**
 * Format a date/time in a specific timezone
 * @param date - The date to format (can be Date object or ISO string)
 * @param timezone - The timezone (e.g., 'Asia/Kolkata', 'America/New_York')
 * @param formatStr - The format string (e.g., 'h:mm a', 'EEEE, MMMM d, yyyy')
 * @returns Formatted date string in the specified timezone
 */
export const formatInTimezone = (
  date: Date | string,
  timezone: string,
  formatStr: string = 'h:mm a'
): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (!timezone) {
      return format(dateObj, formatStr);
    }
    
    // Check if timezone is supported
    try {
      if (!Intl.supportedValuesOf('timeZone').includes(timezone)) {
        console.warn(`Timezone ${timezone} not supported, using local timezone`);
        return format(dateObj, formatStr);
      }
    } catch (e) {
      // Intl.supportedValuesOf might not be available in all browsers
      // Continue with timezone formatting attempt
    }
    
    // Convert formatStr to Intl.DateTimeFormat options
    // For common formats like 'h:mm a', we'll use Intl.DateTimeFormat
    if (formatStr === 'h:mm a') {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      return formatter.format(dateObj);
    }
    
    if (formatStr === 'EEEE, MMMM d, yyyy') {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return formatter.format(dateObj);
    }
    
    if (formatStr === 'MMMM d, yyyy') {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return formatter.format(dateObj);
    }
    
    // For other formats, use Intl.DateTimeFormat to get formatted parts and reconstruct
    // This is a fallback for formats not explicitly handled above
    // We'll use a simpler approach: format the date parts in the timezone and use date-fns for formatting
    
    // Get date parts in the target timezone
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    }).formatToParts(dateObj);
    
    const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const month = parseInt(parts.find(p => p.type === 'month')?.value || '1') - 1;
    const day = parseInt(parts.find(p => p.type === 'day')?.value || '1');
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');
    
    // Create a date object representing the time in the target timezone
    // Note: This creates a date in local timezone but with values from target timezone
    // For display purposes, this works correctly with date-fns formatting
    const adjustedDate = new Date(year, month, day, hour, minute, second);
    
    return format(adjustedDate, formatStr);
  } catch (error) {
    console.warn('Error formatting date in timezone:', error);
    // Fallback to local timezone formatting
    return format(typeof date === 'string' ? new Date(date) : date, formatStr);
  }
};

/**
 * Get a formatted time range in a specific timezone
 * @param startDate - Start date/time
 * @param endDate - End date/time (or duration in minutes)
 * @param timezone - The timezone
 * @returns Formatted time range string (e.g., "8:30 AM - 9:30 AM")
 */
/** Short timezone label, e.g. `IST` for `Asia/Calcutta`. */
export const getTimezoneShortName = (timezone: string): string => {
  if (!timezone.trim()) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;
  } catch {
    return timezone;
  }
};

/** Human-readable label, e.g. `Asia/Calcutta (IST)`. */
export const formatTimezoneLabel = (timezone: string): string => {
  const trimmed = timezone.trim();
  if (!trimmed) return '';
  const shortName = getTimezoneShortName(trimmed);
  return shortName && shortName !== trimmed ? `${trimmed} (${shortName})` : trimmed;
};

export const formatTimeRangeInTimezone = (
  startDate: Date | string,
  endDate: Date | string | number,
  timezone: string
): string => {
  const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  let endDateObj: Date;
  
  if (typeof endDate === 'number') {
    // endDate is duration in minutes
    endDateObj = new Date(startDateObj.getTime() + endDate * 60000);
  } else {
    endDateObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
  }
  
  const startTime = formatInTimezone(startDateObj, timezone, 'h:mm a');
  const endTime = formatInTimezone(endDateObj, timezone, 'h:mm a');
  
  return `${startTime} - ${endTime}`;
};

